import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router'
import { useI18n } from '../i18n/useI18n'
import { formatCalendarDate, formatMegabytes } from '../i18n/format'
import type { I18nContextValue, Lang } from '../i18n/context'
import { fetchReleaseManifest } from '../data/releaseManifest'
import type { ReleaseManifest } from '../data/releaseManifest'
import { installerUrl, releaseManifestUrl } from '../data/downloads'
import { legalPages } from '../data/legal/legalPages'
import { ACCOUNT_PATHS } from '../account/accountPaths'
import PriceStatusBadge from '../components/PriceStatusBadge'
import { formatNok } from '../data/pricing'
import type { PricingApp } from '../data/pricing'
import './download.css'

type Translate = I18nContextValue['t']
type ManifestState = { status: 'loading' } | { status: 'ready'; manifest: ReleaseManifest } | { status: 'failed'; message: string }

const STEP_KEYS: readonly string[] = ['install', 'smartscreen', 'signIn']
const REQUIREMENT_KEYS: readonly string[] = ['os', 'runtime', 'network']
const CONTACT_PATH: string = '/contact'
const PRICES_PATH: string = '/products'

function useReleaseManifest(appId: string): ManifestState {
  const [state, setState] = useState<ManifestState>({ status: 'loading' })
  useEffect(() => {
    const controller: AbortController = new AbortController()
    fetchReleaseManifest(releaseManifestUrl(appId), controller.signal)
      .then((manifest: ReleaseManifest) => setState({ status: 'ready', manifest }))
      .catch((error: unknown) => { if (!controller.signal.aborted) setState({ status: 'failed', message: (error as Error).message }) })
    return () => controller.abort()
  }, [appId])
  return state
}

function ReleaseFacts({ manifest, lang, t }: { manifest: ReleaseManifest; lang: Lang; t: Translate }): ReactElement {
  return (
    <dl className="dl-facts">
      <div><dt>{t('download.version')}</dt><dd>{manifest.version}</dd></div>
      <div><dt>{t('download.released')}</dt><dd>{formatCalendarDate(manifest.releasedAt, lang)}</dd></div>
      <div><dt>{t('download.size')}</dt><dd>{formatMegabytes(manifest.sizeBytes, lang)}</dd></div>
      <div><dt>{t('download.file')}</dt><dd className="dl-mono">{manifest.fileName}</dd></div>
    </dl>
  )
}

function ReleaseCard({ app, state, lang, t }: { app: PricingApp; state: ManifestState; lang: Lang; t: Translate }): ReactElement {
  if (state.status === 'loading') return <div className="dl-card dl-card--loading" aria-busy="true"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> {t('download.loading')}</div>
  if (state.status === 'failed') {
    return (
      <div className="dl-card dl-card--failed" role="alert">
        <p className="dl-failed-title"><i className="bi bi-exclamation-triangle" aria-hidden="true" /> {t('download.failed')}</p>
        <code className="dl-failed-detail">{state.message}</code>
        <p className="dl-failed-help">{t('download.failedHelp')} <Link to={CONTACT_PATH}>{t('download.contact')}</Link>.</p>
      </div>
    )
  }
  return (
    <div className="dl-card">
      <a className="btn btn-primary btn-lg dl-button" href={installerUrl(app.id, state.manifest.fileName)} download>
        <i className="bi bi-download" aria-hidden="true" /> {t('download.button')}
      </a>
      <ReleaseFacts manifest={state.manifest} lang={lang} t={t} />
      <div className="dl-hash">
        <span className="dl-label">SHA-256</span>
        <code className="dl-mono dl-hash-value">{state.manifest.sha256}</code>
        <span className="dl-hint">{t('download.sha256Hint')}</span>
      </div>
    </div>
  )
}

function GettingStarted({ t }: { t: Translate }): ReactElement {
  return (
    <section className="dl-block">
      <h2 className="dl-heading">{t('download.stepsHeading')}</h2>
      <ol className="dl-steps">
        {STEP_KEYS.map((k) => (
          <li key={k} className="dl-step">
            <h3 className="dl-step-title">{t(`download.steps.${k}.title`)}</h3>
            <p className="dl-step-body">{t(`download.steps.${k}.body`)}</p>
          </li>
        ))}
      </ol>
    </section>
  )
}

function AccessBlock({ app, t }: { app: PricingApp; t: Translate }): ReactElement {
  return (
    <section className="dl-block dl-access">
      <h2 className="dl-heading">{t('download.access.heading')}</h2>
      <p className="dl-price"><span className="dl-price-num">kr {formatNok(app.priceNok)}</span> {t('pricing.perMonth')} {app.priceStatus && <PriceStatusBadge status={app.priceStatus} />}</p>
      <p className="dl-hint">{t('pricing.footnote')}</p>
      <p>{t('download.access.body')}</p>
      <div className="dl-access-links">
        <Link className="btn btn-outline-primary" to={CONTACT_PATH}>{t('download.access.contact')}</Link>
        <Link to={ACCOUNT_PATHS.register}>{t('download.access.createAccount')}</Link>
        <Link to={PRICES_PATH}>{t('download.access.prices')}</Link>
      </div>
      <p className="dl-hint dl-terms">{t('pricing.cta.termsLead')} <Link to={legalPages.terms.path}>{t('pricing.cta.termsLink')}</Link>.</p>
    </section>
  )
}

function Requirements({ app, t }: { app: PricingApp; t: Translate }): ReactElement {
  return (
    <section className="dl-block">
      <h2 className="dl-heading">{t('download.requirementsHeading')}</h2>
      <ul className="dl-requirements">
        {REQUIREMENT_KEYS.map((k) => <li key={k}><i className="bi bi-check2" aria-hidden="true" /> {t(`${app.i18nKey}.requirements.${k}`)}</li>)}
      </ul>
    </section>
  )
}

export default function Download({ app }: { app: PricingApp }): ReactElement {
  const { lang, t } = useI18n()
  const state: ManifestState = useReleaseManifest(app.id)

  return (
    <section className="section dl-page">
      <div className="container">
        <header className="dl-head">
          <div className="dl-eyebrow"><i className="bi bi-windows" aria-hidden="true" /> {t('download.eyebrow')}</div>
          <h1 className="dl-title">{t(`${app.i18nKey}.name`)}</h1>
          <p className="dl-tagline">{t(`${app.i18nKey}.tagline`)}</p>
          <p className="dl-pitch">{t(`${app.i18nKey}.pitch`)}</p>
        </header>
        <div className="row g-4 g-lg-5">
          <div className="col-lg-6 order-lg-2">
            <ReleaseCard app={app} state={state} lang={lang} t={t} />
            <AccessBlock app={app} t={t} />
          </div>
          <div className="col-lg-6 order-lg-1">
            <GettingStarted t={t} />
            <Requirements app={app} t={t} />
          </div>
        </div>
      </div>
    </section>
  )
}
